import React, { useEffect, useState } from 'react'
import './loading.css'
import { LoadingScreenBackdrop } from './LoadingScreenBackdrop'
import { ECS_LOADING_ICON_URL } from '../utils/vercelAssetPreloadGate'
import type { GatePreloadDetail } from '../utils/vercelAssetPreloadGate'
import { peekLoadingMessage, subscribeLoadingMessage } from '../constants/loadingMessages'

/**
 * The loading screen, shared by cold boot (`VercelAssetBootstrap`) and every per-route asset gate
 * (`RouteAssetGate`).
 *
 * Extracted rather than duplicated because route gates show it far more often than cold boot does:
 * any drift between the two would read to a player as two different loading screens in one session.
 * Purely presentational — it owns no loading logic.
 */
export type GameLoadingScreenProps = {
  /**
   * `full` — cold boot only: the cross-fading artwork slideshow behind the gem.
   * `compact` — every other loading screen: spark background + floating gem, matching
   * `FireLoading`. Route gates appear many times a session, and the slideshow reads as "the game
   * is starting up" — showing it on a navigation makes a short wait feel like a relaunch.
   */
  variant?: 'full' | 'compact'
  /** 0-100. Pass 0 with `total === 0` for the indeterminate "Loading…" state. */
  pct: number
  /** Drives the percentage suffix; 0 means "we do not know the size yet". */
  total: number
  /** Raw per-asset detail. Only ever rendered under `debug`. */
  detail?: GatePreloadDetail | null
  /** Destination-appropriate opening line; the rotation continues from there. */
  firstMessage?: string
  /** `?debugload=1` — swap the status line for raw asset paths and timings. */
  debug?: boolean
  /** Overrides the bottom label entirely (used by the PWA install phase). */
  labelOverride?: React.ReactNode
}

export const GameLoadingScreen: React.FC<GameLoadingScreenProps> = ({
  variant = 'full',
  pct,
  total,
  detail = null,
  firstMessage,
  debug = false,
  labelOverride,
}) => {
  /**
   * The script lives outside the component (see `subscribeLoadingMessage`): cold boot and the route
   * gates each mount their own copy of this screen, and a per-mount timer makes the copy lurch
   * whenever one hands over to the other.
   */
  const [message, setMessage] = useState(() => peekLoadingMessage(firstMessage))

  useEffect(() => {
    const { current, unsubscribe } = subscribeLoadingMessage(firstMessage, setMessage)
    setMessage(current)
    return unsubscribe
  }, [firstMessage])

  return (
    <div className="game-loading">
      <div className="game-loading-bg" />
      {variant === 'full' && (
        <>
          <LoadingScreenBackdrop />
          <div className="game-loading-embers" aria-hidden />
        </>
      )}
      <div className="game-loading-stars">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="game-loading-star" style={{ '--i': i } as React.CSSProperties} />
        ))}
      </div>
      <div className="game-loading-center">
        <div className="game-loading-icon-box">
          <div className="game-loading-icon-wrap game-loading-icon-wrap--float">
            <img
              src={ECS_LOADING_ICON_URL}
              alt=""
              className="game-loading-icon game-loading-icon--static"
              draggable={false}
            />
          </div>
        </div>
      </div>
      <div
        style={{
          position: 'absolute',
          left: '50%',
          bottom: '12%',
          transform: 'translateX(-50%)',
          width: 'min(420px, 86vw)',
          textAlign: 'center',
          zIndex: 10,
        }}
      >
        <div className="gl-progress-detail" aria-hidden>
          {debug && detail ? (
            <>
              <p className="gl-progress-detail-line">
                <span className="gl-progress-detail-group">{detail.group || 'assets'}</span>
                <span className="gl-progress-detail-name">{detail.name}</span>
              </p>
              <p className="gl-progress-detail-meta">
                {detail.inFlight} in flight
                {detail.slowest.length > 0 && (
                  <>
                    {' · slowest '}
                    <span className="gl-progress-detail-slow">
                      {detail.slowest[0].label} {(detail.slowest[0].ms / 1000).toFixed(1)}s
                    </span>
                  </>
                )}
              </p>
            </>
          ) : (
            <p className="gl-progress-phase" key={message}>
              {message}
            </p>
          )}
        </div>
        <div
          className="gl-progress-track"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={total > 0 ? pct : undefined}
          aria-label="Loading progress"
        >
          <div className="gl-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <p className="gl-progress-label">
          {labelOverride ?? (
            total > 0 ? (
              <>
                Loading… <b>{pct}%</b>
              </>
            ) : (
              'Loading…'
            )
          )}
        </p>
      </div>
    </div>
  )
}
